import { useState, useEffect, useCallback } from 'react';
import { Grid, Box, Paper } from '@mui/material';
import { FieldErrors, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import FormButtons from '../../../../../app/components/FormButtons';
import { useMenuForm } from '../../hooks/useMenuForm';
import { fetchMenuFormDataAsync } from '../../store/menuSlice';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../app/store/configureStore';
import { useNavigate, useParams } from 'react-router-dom';
import ListTitleBox from '../../../../../app/components/list/ListTitleBox';
import LoadingComponent from '../../../../../app/layout/LoadingComponent';
import ErrorComponent from '../../../../../app/layout/ErrorComponent';
import { menuValidationSchema } from '../../menuValidation';
import {
  LanguageCode,
  LocalizedLanguageFormData,
} from '../../../../../app/data/models/common/localizedLanguage';
import { ObjectSchema } from 'yup';
import { useRestaurant } from '../../../../../app/hooks/useRestaurant';
import { MenuFormData, toMenuFormData } from '../../types';
import { LanguageFormTabs } from '../../../../../app/components/LanguageFormTabs';

export const MenuForm = () => {
  const { restaurantId: restaurantIdString, menuId: menuIdString } =
    useParams();
  const restaurantId = restaurantIdString || '';
  const menuId = menuIdString || undefined;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { handleFormSubmit, isSubmitting } = useMenuForm(restaurantId);
  const [menu, setMenu] = useState<MenuFormData | null>(null);
  const { menuFormDataStatus } = useAppSelector((state) => state.menus);
  const {
    restaurant,
    isLoading: isRestaurantLoading,
    error: restaurantError,
    retry: retryRestaurant,
  } = useRestaurant(restaurantId);
  const [activeTab, setActiveTab] = useState<number>(0);

  const defaultValues: MenuFormData = {
    titles: (restaurant?.languageCodes || []).reduce(
      (acc, code) => ({
        ...acc,
        [code]: '',
      }),
      {} as LocalizedLanguageFormData
    ),
  };

  const { control, handleSubmit, reset } = useForm<MenuFormData>({
    mode: 'onTouched',
    resolver: yupResolver(
      menuValidationSchema(
        t,
        (restaurant?.languageCodes || []) as LanguageCode[]
      ) as ObjectSchema<MenuFormData>
    ),
    defaultValues,
  });

  const fetchMenuFormData = async (menuId: string) => {
    try {
      const menuDetails = await dispatch(
        fetchMenuFormDataAsync({ restaurantId, menuId })
      ).unwrap();
      const menuFormData = toMenuFormData(menuDetails);
      setMenu(menuFormData);
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        toast.error(
          error.response?.data?.title || error.message || t('common.error')
        );
      } else {
        toast.error((error as Error).message || t('common.error'));
      }
    }
  };

  useEffect(() => {
    if (menuId) {
      fetchMenuFormData(menuId);
    }
  }, [menuId, dispatch, restaurantId]);

  useEffect(() => {
    if (menu && restaurant?.languageCodes) {
      const formData = {
        titles: restaurant.languageCodes.reduce(
          (acc, code) => ({
            ...acc,
            [code]: menu.titles?.[code] || '',
          }),
          {} as LocalizedLanguageFormData
        ),
      };
      reset(formData);
    }
  }, [menu, reset, restaurant]);

  const handleBack = useCallback(() => {
    navigate(`/manager/restaurants/${restaurantId}/menus`);
  }, [navigate, restaurantId]);

  const onSubmit = async (data: MenuFormData) => {
    try {
      await handleFormSubmit(data, menuId);
      toast.success(menu ? t('menu.update.success') : t('menu.create.success'));
      navigate(`/manager/restaurants/${restaurantId}/menus`);
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        toast.error(
          error.response?.data?.title || error.message || t('common.error')
        );
      } else {
        toast.error((error as Error).message || t('common.error'));
      }
    }
  };

  const onError = (errors: FieldErrors<MenuFormData>) => {
    if (!restaurant?.languageCodes?.length) return;

    const titleErrors = errors.titles;
    if (titleErrors) {
      const errorLanguageIndex = restaurant.languageCodes.findIndex(
        (code) => titleErrors[code]
      );

      if (errorLanguageIndex >= 0) {
        setActiveTab(errorLanguageIndex);
      }
    }
  };

  const header = (
    <ListTitleBox
      title={menuId ? t('menu.form.edit.title') : t('menu.form.create.title')}
      onBackClick={handleBack}
    />
  );

  if (isRestaurantLoading) return <LoadingComponent />;
  if (restaurantError || !restaurant)
    return <ErrorComponent header={header} onPrimaryAction={retryRestaurant} />;

  if (menuFormDataStatus === 'loading' && menuId)
    return <LoadingComponent header={header} />;

  if (menuFormDataStatus === 'failed' && menuId)
    return (
      <ErrorComponent
        header={header}
        title={t('errors.generic.title')}
        description={t('errors.generic.description')}
        primaryButtonText={t('common.retry')}
        secondaryButtonText={t('common.back')}
        onPrimaryAction={() => fetchMenuFormData(menuId)}
      />
    );

  return (
    <>
      {header}
      <Box component={Paper} sx={{ width: '100%', mb: 3, p: 2 }}>
        <form onSubmit={handleSubmit(onSubmit, onError)}>
          <Grid
            container
            spacing={{ xs: 2, sm: 3 }}
            sx={{ mb: { xs: 2, sm: 3 } }}
          >
            <Grid item xs={12}>
              <LanguageFormTabs
                languageCodes={restaurant.languageCodes}
                control={control}
                activeTab={activeTab}
                onTabChange={setActiveTab}
                fields={[
                  {
                    name: 'titles',
                    label: t('menu.form.fields.title'),
                  },
                ]}
              />
            </Grid>
          </Grid>
          <FormButtons
            loading={isSubmitting}
            submitText={menuId ? t('common.update') : t('common.create')}
          />
        </form>
      </Box>
    </>
  );
};
